import { useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeys } from '~/api/queryKeys';
import { useTaxonomyStudioStore } from '~/store/taxonomy-studio.store';

import {
  discardDraft,
  publishDraft,
  restoreRelease,
  revertChange,
  revertChanges,
} from '../services/taxonomy-write.service';

import { useTaxonomyMutation } from './use-taxonomy-write';

/** Clears the draft bookkeeping and reloads every taxonomy read (publish / discard). */
function useDraftClosed() {
  const qc = useQueryClient();
  return () => {
    useTaxonomyStudioStore.getState().reset();
    void qc.invalidateQueries({ queryKey: queryKeys.taxonomyStudio.all });
  };
}

/** Publishes the draft against the version the user reviewed (409 DRAFT_CHANGED if it moved). */
export function usePublishDraft() {
  const closed = useDraftClosed();
  return useMutation({
    mutationFn: publishDraft,
    onSuccess: closed,
  });
}

export function useDiscardDraft() {
  const closed = useDraftClosed();
  return useMutation({
    mutationFn: discardDraft,
    onSuccess: closed,
  });
}

/** Reverts one draft change back to its live state. */
export function useRevertChange() {
  return useTaxonomyMutation((changeId: string) => revertChange(changeId));
}

/** Reverts several draft changes in one action. */
export function useRevertChanges() {
  return useTaxonomyMutation((changeIds: string[]) => revertChanges(changeIds));
}

/** Prepares a ROLLBACK draft that puts the taxonomy back to release `seq`. */
export function useRestoreRelease(options: { onError?: (err: unknown) => boolean } = {}) {
  return useTaxonomyMutation(
    ({ seq, name, reason }: { seq: number; name?: string; reason?: string }) =>
      restoreRelease(seq, { name, reason }),
    options.onError ? { onError: options.onError } : {},
  );
}
